import React, { useEffect, useMemo, useState } from "react";
import { TextField as MUITextField } from "@mui/material";
import MuiAutocomplete from "@mui/material/Autocomplete";
import { AxiosResponse } from "axios";
import throttle from "lodash/throttle";
import queryString from "query-string";

import { axios } from "../api";
import type { AeronetV6ItemsResponse } from "../types";

export interface PartOption {
  id: number;
  number: string | null;
  name: string | null;
}

interface Props {
  value: PartOption | null;
  onChange: (part: PartOption | null) => void;
  label?: string;
  disabled?: boolean;
  error?: boolean;
  helperText?: React.ReactNode;
}

const partLabel = (p: PartOption) => [p.number, p.name].filter(Boolean).join(" — ") || `#${p.id}`;

/**
 * Part FK autocomplete over the parts catalogue (GET /v6/parts, active parts,
 * searched by `q`) — hands the caller the selected {id, number, name}.
 *
 * The sors / quotes / invoices PartAutocompletes are Formik fields that write
 * module-shaped side fields (part_number, lines.0.part_id …), unsuitable for
 * reuse; this is the standalone shape of components/AircraftAutocomplete.
 */
export default function PartAutocomplete({
  value,
  onChange,
  label = "Part",
  disabled = false,
  error,
  helperText,
}: Props) {
  const [options, setOptions] = useState<PartOption[]>([]);
  const [inputValue, setInputValue] = useState("");

  const fetchParts = useMemo(
    () =>
      throttle((q: string, callback: (rows: PartOption[]) => void) => {
        const params = {
          filter: [JSON.stringify({ field: "active", op: "==", value: true })],
          q,
        };
        axios
          .get("/v6/parts", {
            params,
            paramsSerializer: (p) => queryString.stringify(p),
            cache: false,
          })
          .then((resp: AxiosResponse<AeronetV6ItemsResponse<PartOption>>) =>
            callback([...(resp.data.items ?? [])])
          )
          .catch(() => callback([]));
      }, 200),
    []
  );

  useEffect(() => {
    let active = true;
    fetchParts(inputValue, (rows) => {
      if (active) setOptions(rows);
    });
    return () => {
      active = false;
    };
  }, [inputValue, fetchParts]);

  return (
    <MuiAutocomplete<PartOption, false, false, false>
      fullWidth
      disabled={disabled}
      options={options}
      value={value}
      onChange={(_e, next) => onChange(next)}
      onInputChange={(_e, next) => setInputValue(next)}
      getOptionLabel={partLabel}
      isOptionEqualToValue={(option, v) => option.id === v.id}
      filterOptions={(x) => x}
      renderInput={(params) => (
        <MUITextField {...params} label={label} error={error} helperText={helperText} fullWidth />
      )}
    />
  );
}
